import { api, PartipostApiResponse, PartipostApiPaginationWrapper } from './'
import {
    SubmissionState,
    SubmissionsPageListRequestPayload,
    BatchSubmissionsPageListRequestPayload,
    DropoutsListRequestPayload,
    RejectedListRequestPayload,
    RejectSubmissionPayload,
    ApproveSubmissionPayload,
    RequestEditsForSubmissionPayload,
    ReviewPendingActionPayload,
    UpdateSubmissionPerformancePayload,
    PaySubmissionPayload,
    RejectSubmissionPayoutPayload,
    DiscussSubmissionPayload,
    VoteSubmissionPayload,
    VotesState,
    MassSubmissionPayload,
    UpdateCompletedSchedulePayload,
    BatchSubmissionState,
    SUBMISSION_VIEW_ENUM,
    PartiposterProfile,
    MassPayoutRequest,
    EditRequestHistoryState,
    ExtendFirstStepDeadlineRequestPayload,
    ReviewCaptionRequestPayload,
    MassRejectAllSubmissionPayload,
} from '../../state/submissions'
import { MAX_PAGE_SIZE } from 'state/review'
import { SubmissionFiltersResponse } from './campaigns'

export interface Submission extends Omit<SubmissionState, 'partiposterId'> {
    partiposter_profile: PartiposterProfile //will be null if is batch current_progress
}
export type SubmissionsListResponse = PartipostApiPaginationWrapper<Submission[]>

export interface BatchSubmission extends Omit<BatchSubmissionState, 'partiposterId'> {
    partiposter_profile: PartiposterProfile //will be null if is batch current_progress
}
export type BatchSubmissionsListResponse = PartipostApiPaginationWrapper<BatchSubmission[]>

export const getSubmissionsPageList = (payload: SubmissionsPageListRequestPayload) => {
    const { offset, campaignUuid, stepUuid, submissionView, vote_type, keywords, tags, resubmitted } = payload
    const parameters = {
        offset: offset,
        step: stepUuid,
        task_status: submissionView,
        limit: MAX_PAGE_SIZE,
        vote_type,
        keywords,
        tags,
        resubmitted,
    }
    return api.get<PartipostApiResponse<SubmissionsListResponse>>(
        `campaigns/${campaignUuid}/submission-progress/`,
        parameters,
    )
}

export const getSubmissionsPageFilters = (payload: SubmissionsPageListRequestPayload) => {
    return api.get<PartipostApiResponse<SubmissionFiltersResponse>>(
        `campaigns/${payload.campaignUuid}/submission-progress/filters/`,
        { step: payload.stepUuid, task_status: payload.submissionView },
    )
}

export const getBatchSubmissionsPageList = (payload: BatchSubmissionsPageListRequestPayload) => {
    const { offset, campaignUuid, keywords, ordering, filteringParams } = payload
    const parameters = {
        offset: offset,
        limit: MAX_PAGE_SIZE,
        keywords,
        ordering: ordering,
        ...filteringParams,
    }
    return api.get<PartipostApiResponse<BatchSubmissionsListResponse>>(
        `campaigns/${campaignUuid}/submissions/`,
        parameters,
    )
}

export const getBatchSubmissionsPageFilters = (payload: BatchSubmissionsPageListRequestPayload) => {
    return api.get<PartipostApiResponse<SubmissionFiltersResponse>>(
        `campaigns/${payload.campaignUuid}/submissions/filters/`,
    )
}

export const getDropoutsList = (payload: DropoutsListRequestPayload) => {
    const { offset, campaignUuid, keywords, type } = payload
    const parameters = {
        offset,
        limit: MAX_PAGE_SIZE,
        keywords,
    }
    const endpoint =
        type === 'submission'
            ? `campaigns/${campaignUuid}/submission-dropouts/`
            : type === 'registration'
              ? `campaigns/${campaignUuid}/submission-registration-dropouts/`
              : ''
    return api.get<PartipostApiResponse<SubmissionsListResponse>>(endpoint, parameters)
}

export const getRejectedList = (payload: RejectedListRequestPayload) => {
    const { offset, campaignUuid, keywords, type } = payload
    const parameters = {
        offset,
        limit: MAX_PAGE_SIZE,
        keywords,
    }
    const endpoint =
        type === 'submission'
            ? `campaigns/${campaignUuid}/submission-rejected/`
            : type === 'registration'
              ? `campaigns/${campaignUuid}/submission-registration-rejected/`
              : ''
    return api.get<PartipostApiResponse<SubmissionsListResponse>>(endpoint, parameters)
}

export type RejectSubmissionResponse = SubmissionState

export const rejectSubmission = (payload: RejectSubmissionPayload) => {
    const { campaignUuid, submissionProgressUuid, reason } = payload
    const requestBody = {
        action_name: 'reject',
        comment: reason,
    }
    return api.patch<PartipostApiResponse<SubmissionState>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionProgressUuid}/`,
        requestBody,
    )
}

export const rejectBatchSubmission = (payload: RejectSubmissionPayload) => {
    const { campaignUuid, submissionUuid, reason } = payload
    const requestBody = {
        comment: reason,
    }
    return api.patch<PartipostApiResponse<SubmissionState>>(
        `campaigns/${campaignUuid}/submissions/${submissionUuid}/reject/`,
        requestBody,
    )
}

export type ApproveSubmissionResponse = SubmissionState

export const approveSubmission = (payload: ApproveSubmissionPayload) => {
    const { campaignUuid, submissionProgressUuid, data, comment } = payload
    const requestBody = {
        data,
        action_name: 'approve',
        comment,
    }
    return api.patch<PartipostApiResponse<SubmissionState>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionProgressUuid}/`,
        requestBody,
    )
}

export type UpdateCompletedScheduleResponse = SubmissionState

export const updateCompletedSchedule = (payload: UpdateCompletedSchedulePayload) => {
    const { submissionUuid, data } = payload
    return api.patch<PartipostApiResponse<SubmissionState>>(
        `submission-progress/${submissionUuid}/schedule-post-time/`,
        data,
    )
}

export type MassSubmissionResponse = MassSubmissionPayload

export const massSubmission = (payload: MassSubmissionPayload) => {
    const { campaignUuid, uuids, action_name, comment, post_time_window } = payload

    const requestBody = {
        uuids,
        action_name,
        comment,
        post_time_window,
    }
    return api.post<PartipostApiResponse<MassSubmissionResponse>>(
        `campaigns/${campaignUuid}/submission-progress/mass-action/`,
        requestBody,
    )
}

export type DiscussSubmissionResponse = SubmissionState

export const getDiscussSubmission = (payload: DiscussSubmissionPayload) => {
    const { campaignUuid, submissionUuid } = payload

    return api.get<PartipostApiResponse<DiscussSubmissionResponse>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionUuid}/discussions/`,
    )
}

export const createDiscussSubmission = (payload: DiscussSubmissionPayload) => {
    const { campaignUuid, submissionUuid, ...requestBody } = payload
    return api.post<PartipostApiResponse<DiscussSubmissionResponse>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionUuid}/discussions/`,
        requestBody,
    )
}

export const updateDiscussSubmission = (payload: DiscussSubmissionPayload) => {
    const { campaignUuid, submissionUuid, discussionUuid, ...requestBody } = payload
    return api.patch<PartipostApiResponse<DiscussSubmissionResponse>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionUuid}/discussions/${discussionUuid}/`,
        requestBody,
    )
}

export const deleteDiscussSubmission = (payload: DiscussSubmissionPayload) => {
    const { campaignUuid, submissionUuid, discussionUuid } = payload
    return api.delete<PartipostApiResponse<DiscussSubmissionResponse>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionUuid}/discussions/${discussionUuid}/`,
    )
}

export const voteSubmission = (payload: VoteSubmissionPayload) => {
    const { campaignUuid, submissionProgressUuid, ...requestBody } = payload
    return api.post<PartipostApiResponse<VotesState>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionProgressUuid}/votes/`,
        requestBody,
    )
}

export type RequestEditsForSubmissionResponse = SubmissionState

export const requestEditsForSubmission = (payload: RequestEditsForSubmissionPayload) => {
    const { campaignUuid, submissionProgressUuid, reason, data } = payload
    const requestBody = {
        action_name: 'request_edit',
        comment: reason,
        data,
    }
    return api.patch<PartipostApiResponse<SubmissionState>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionProgressUuid}/`,
        requestBody,
    )
}

export type ReviewPendingActionResponse = SubmissionState

export const reviewPendingAction = (payload: ReviewPendingActionPayload) => {
    const { campaignUuid, submissionUuid, actionType } = payload
    const requestBody = {
        action_name: actionType === 'approve' ? 'approve_pending_action' : 'reject_pending_action',
        data: {},
    }
    return api.put<PartipostApiResponse<SubmissionState>>(
        `campaigns/${campaignUuid}/submission-progress/${submissionUuid}/`,
        requestBody,
    )
}

export const updateSubmissionPerformance = (payload: UpdateSubmissionPerformancePayload) => {
    const { campaignUuid, submissionUuid, submissionTasks } = payload
    return api.patch<PartipostApiResponse<any>>(`campaigns/${campaignUuid}/submission-performance/${submissionUuid}/`, {
        submission_tasks: submissionTasks,
    })
}

export const rejectSubmissionPayout = (payload: RejectSubmissionPayoutPayload) => {
    const { campaignUuid, submissionUuid } = payload
    return api.patch<PartipostApiResponse<any>>(
        `campaigns/${campaignUuid}/submission-performance/${submissionUuid}/reject/`,
    )
}

export const paySubmission = (payload: PaySubmissionPayload) => {
    const { campaignUuid, submissionUuid } = payload
    return api.patch<PartipostApiResponse<any>>(
        `campaigns/${campaignUuid}/submission-performance/${submissionUuid}/pay/`,
    )
}

export interface MassPayoutSubmissionError {
    username: string
    error: string
}
export interface MassPayoutSubmissionByUsernameResponse {
    success_count: number
    errors: MassPayoutSubmissionError[]
}

export const massPayoutSubmission = (payload: MassPayoutRequest) => {
    const { campaignUuid, usernames, platform } = payload
    return api.post<PartipostApiResponse<MassPayoutSubmissionByUsernameResponse>>(
        `campaigns/${campaignUuid}/submission-performance/mass-payout-by-platform-username/`,
        { usernames, platform },
    )
}
// Base payload type for all export operations
export interface BaseExportSubmissionsPayload {
    uuid: string
}

// Regular submissions export payload (extends base with filtering options)
export interface RequestExportSubmissionsPayload extends BaseExportSubmissionsPayload {
    viewType:
        | SUBMISSION_VIEW_ENUM.PENDING
        | SUBMISSION_VIEW_ENUM.COMPLETED
        | SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
        | SUBMISSION_VIEW_ENUM.FOR_REVIEW
    stepUuid?: string
}

// Batch submissions export payload (uses base type only)
export type RequestExportBatchSubmissionsPayload = BaseExportSubmissionsPayload

export const exportSubmissions = (payload: RequestExportSubmissionsPayload) => {
    const { uuid, viewType, stepUuid } = payload
    switch (viewType) {
        case SUBMISSION_VIEW_ENUM.PENDING:
        case SUBMISSION_VIEW_ENUM.FOR_REVIEW:
        case SUBMISSION_VIEW_ENUM.EDITS_REQUESTED:
        case SUBMISSION_VIEW_ENUM.COMPLETED:
            return api.get<PartipostApiResponse>(
                `campaigns/${uuid}/submission-progress/export/?step=${stepUuid}&task_status=${viewType}`,
            )
    }
}

export const exportBatchSubmissions = (payload: RequestExportBatchSubmissionsPayload) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse>(`campaigns/${uuid}/submissions/export/`)
}

export interface GetEditRequestHistoryListRequestPayload {
    submissionProgressUuid: string
}

export interface GetEditRequestHistoryListResponse {
    results: EditRequestHistoryState[]
}

export const getEditRequestHistoryList = (payload: GetEditRequestHistoryListRequestPayload) => {
    const { submissionProgressUuid } = payload
    return api.get<PartipostApiResponse<GetEditRequestHistoryListResponse>>(
        `submission-progress/${submissionProgressUuid}/request-edit-history/`,
    )
}

export const extendFirstStepDeadline = (payload: ExtendFirstStepDeadlineRequestPayload) => {
    const { stepUuid } = payload
    return api.post<PartipostApiResponse<any>>(`steps/${stepUuid}/extend-deadline/`)
}

export const reviewCaption = (payload: ReviewCaptionRequestPayload) => {
    const { submissionProgressUuid, caption } = payload
    const requestBody = {
        caption: caption,
    }
    return api.post<PartipostApiResponse<SubmissionState>>(
        `submission-progress/${submissionProgressUuid}/retry-ai-caption-review/`,
        requestBody,
    )
}

export type RejectedDropoutsListResponse = SubmissionsListResponse

export const getRejectedAndDropoutList = (payload: { campaignUuid: string; offset?: number; keywords?: string }) => {
    const { campaignUuid, offset, keywords } = payload
    const parameters = {
        offset,
        limit: MAX_PAGE_SIZE,
        keywords,
    }
    return api.get<PartipostApiResponse<RejectedDropoutsListResponse>>(
        `campaigns/${campaignUuid}/submission-rejected-dropouts/`,
        parameters,
    )
}
export interface MassRejectAllSubmissionResponse {
    success_count: number
    errors: {
        uuid: string
        error: string
    }[]
}

export const massRejectAllSubmission = (payload: MassRejectAllSubmissionPayload) => {
    const { campaignUuid, uuids, comment } = payload

    return api.post<PartipostApiResponse<MassRejectAllSubmissionResponse>>(
        `campaigns/${campaignUuid}/submissions/mass-reject/`,
        {
            uuids,
            comment,
        },
    )
}
